import type { PlanType } from "../const/planConst";
import { formatMoney } from "../utils/deadSubscriptions";
import type { BillingSubscription } from "./pastDueSubscription";
import { findPastDueSubscription } from "./pastDueSubscription";

// "failed-payment-wall" experiment (klap-api helpers/failed_payment_wall.ts):
// from day 3 after a failed renewal, the test arm can't create, export or
// publish until the card is fixed; klap-api answers those requests with a 402
// `subscription_payment_failed` and this app shows the wall
// (components/FailedPaymentWall.tsx). klap-api evaluates the arm, the only
// place it is evaluated: GET /billing/subscription returns it as
// `failed_payment_wall`, so the app never reads the flag itself. Control, and
// days 0-2, keep SubscriptionIssueBanner as it was. Delete with the
// experiment.

export const FAILED_PAYMENT_WALL_ERROR_CODE = "subscription_payment_failed";

export type FailedPaymentWallAction = "task" | "export" | "publish";

// What GET /billing/subscription adds to a past-due B2C subscription.
export type FailedPayment = {
  subscription_id: string;
  invoice_id: string | null;
  /** Unix seconds of the failed invoice's first payment attempt. */
  failed_at: number;
  /** Minor units. */
  amount_due: number | null;
  currency: string | null;
  plan: string;
  hosted_invoice_url: string | null;
};

export type FailedPaymentWallState = {
  variant: "control" | "test" | null;
  blocked: boolean;
  days_past_due: number;
  blocked_from_day: number;
};

export type ListedSubscription = BillingSubscription & {
  id?: string;
  /** klap-api's plan for the price: null for the metered API plan. */
  plan?: string | null;
  /** Any past-due Klap plan's, even one the wall doesn't act on. */
  failed_payment?: FailedPayment | null;
};

export type SubscriptionsResponse = {
  subscriptions?: ListedSubscription[];
  /** The failed payment to pay (klap-api wallFailedPayment): the profile's
   *  own past-due Klap plan, null when there's none, e.g. for an old
   *  past-due plan a newer active plan replaced (paying it would charge the
   *  customer twice). Absent from an older API and from a customer-less
   *  response: see hasWallVerdict. */
  failed_payment?: FailedPayment | null;
  failed_payment_wall?: FailedPaymentWallState | null;
};

// klap-api's B2C plans (isB2CPlan in helpers/failed_payment_wall.ts), the
// ones a failed payment is about. Anything else that's past due is the
// metered API plan.
const B2C_PLANS: PlanType[] = [
  "basic",
  "pro",
  "pro-plus",
  "basic-yearly",
  "pro-yearly",
  "pro-plus-yearly",
];
export const isB2CPlan = (plan?: string | null): boolean =>
  B2C_PLANS.includes(plan as PlanType);

// The top-level failed_payment is klap-api's verdict, null included. Without
// it (an older API) every past-due subscription counts, as before, so a real
// failure is never hidden.
const hasWallVerdict = (
  response?: SubscriptionsResponse | null,
): response is SubscriptionsResponse & { failed_payment: FailedPayment | null } =>
  !!response && "failed_payment" in response;

// A past-due or unpaid subscription outside the Klap plans (the metered API
// plan): klap-api sends it to the billing portal's add-a-card page.
const isOtherPaymentDue = (subscription: ListedSubscription) =>
  (subscription.status === "past_due" || subscription.status === "unpaid") &&
  !isB2CPlan(subscription.plan);

// The failed renewal the user can pay: klap-api's verdict, or with an older
// API the first past-due Klap plan's.
export const findFailedPayment = (
  response?: SubscriptionsResponse | null,
): FailedPayment | undefined =>
  hasWallVerdict(response)
    ? response.failed_payment ?? undefined
    : response?.subscriptions?.find((subscription) => subscription.failed_payment)
        ?.failed_payment ?? undefined;

// Whether "Update payment" has anything to pay, the same answer as PUT
// /billing/subscription (flow "update_payment"): the failed renewal above,
// or a subscription outside the Klap plans that's past due. Not an old
// past-due plan a newer active one replaced: klap-api answers "nothing_due".
// Known gap, klap-api's too (updatePaymentTarget): a replaced plan next to a
// past-due metered plan still gets the add-a-card page, and a new card there
// can pay the replaced plan's open invoice as well. Rare (it takes the
// metered API plan too); closing it changes which page klap-api opens, and
// this follows.
export const hasPaymentDue = (response?: SubscriptionsResponse | null): boolean =>
  hasWallVerdict(response)
    ? !!response.failed_payment || !!response.subscriptions?.some(isOtherPaymentDue)
    : !!findPastDueSubscription(response?.subscriptions);

// The subscription that payment is for, for its dates.
export const findPayableSubscription = (
  response?: SubscriptionsResponse | null,
): ListedSubscription | undefined => {
  if (!hasWallVerdict(response)) return findPastDueSubscription(response?.subscriptions);
  const failedId = response.failed_payment?.subscription_id;
  return response.subscriptions?.find((subscription) =>
    failedId ? subscription.id === failedId : isOtherPaymentDue(subscription),
  );
};

// A past-due subscription nothing is due for: replaced by a newer plan.
export const hasReplacedPastDue = (response?: SubscriptionsResponse | null): boolean =>
  !!findPastDueSubscription(response?.subscriptions) && !hasPaymentDue(response);

// A 402 from a create/export/publish route that the wall answers.
export const isFailedPaymentWallResponse = (
  status: number,
  body?: { error_code?: string } | null,
): boolean => status === 402 && body?.error_code === FAILED_PAYMENT_WALL_ERROR_CODE;

// "Pro", "Pro Plus" or "Basic", yearly or not.
export const planLabel = (plan?: string | null): string | null => {
  if (!plan) return null;
  if (plan.startsWith("pro-plus")) return "Pro Plus";
  if (plan.startsWith("pro")) return "Pro";
  if (plan.startsWith("basic")) return "Basic";
  return null;
};

export const failedAmountLabel = (failedPayment?: FailedPayment | null) =>
  failedPayment?.amount_due && failedPayment.currency
    ? formatMoney(failedPayment.amount_due, failedPayment.currency)
    : null;

// "Your Klap Pro renewal of $29.00 didn't go through." Drops the parts it
// doesn't know rather than guessing them.
export const failedRenewalSentence = (failedPayment?: FailedPayment | null) => {
  const plan = planLabel(failedPayment?.plan);
  const amount = failedAmountLabel(failedPayment);
  return `Your Klap${plan ? ` ${plan}` : ""} renewal${amount ? ` of ${amount}` : ""} didn't go through.`;
};

// The wall opens over whatever the user was doing. FailedPaymentWallHost
// (mounted once by the app providers) listens; flows that don't hold the
// app modal (generation requests that throw) open it through here.
type Listener = (action?: FailedPaymentWallAction) => void;
const listeners = new Set<Listener>();

export const onFailedPaymentWallOpen = (listener: Listener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const openFailedPaymentWall = (action?: FailedPaymentWallAction) => {
  listeners.forEach((listener) => listener(action));
};

// Thrown by request helpers once they opened the wall, so their callers skip
// their own error toast.
export class FailedPaymentWallError extends Error {
  constructor() {
    super("Your renewal payment failed. Update your payment method to keep creating.");
    this.name = "FailedPaymentWallError";
  }
}

export const isFailedPaymentWallError = (error: unknown): boolean =>
  (error as Error | undefined)?.name === "FailedPaymentWallError";

// An error the wall answers: FailedPaymentWallError, or one carrying the
// 402's code (SchedulingBatchError's errorCode).
export const isFailedPaymentWallFailure = (error: unknown): boolean =>
  isFailedPaymentWallError(error) ||
  (error as { errorCode?: string } | undefined)?.errorCode ===
    FAILED_PAYMENT_WALL_ERROR_CODE;

// The error toast's text, or null once the wall opened for this error: the
// wall says what to do, and a toast next to it ("Upgrade Plan") would
// contradict it. toast.promise closes its toast when `error` renders null.
export const errorToastMessage = (
  error: unknown,
  fallback: string,
): string | null =>
  isFailedPaymentWallFailure(error)
    ? null
    : (error as Error | undefined)?.message || fallback;

// `/billing?update_payment=1` (FlyCode's dunning emails link there) opens
// the update-payment action for the signed-in user; `source` defaults to
// email, the only sender today.
export const UPDATE_PAYMENT_PARAM = "update_payment";
export const updatePaymentLandingSource = (
  params: URLSearchParams | null | undefined,
): string | null =>
  params?.get(UPDATE_PAYMENT_PARAM) === "1" ? params.get("source") || "email" : null;

// For request helpers: opens the wall and throws FailedPaymentWallError when
// the response is the wall's 402.
export const throwIfFailedPaymentWall = (
  status: number,
  body: { error_code?: string } | null | undefined,
  action: FailedPaymentWallAction = "task",
) => {
  if (!isFailedPaymentWallResponse(status, body)) return;
  openFailedPaymentWall(action);
  throw new FailedPaymentWallError();
};
